import apiService from './apiService';

/**
 * Service for handling Custom Tools API
 * Manages custom tool discovery, configuration templates, and management
 */
class CustomToolsService {
  /**
   * Get all available custom tools
   * @returns {Promise<Object>} List of available custom tools with metadata
   */
  async getAvailableTools() {
    try {
      const response = await apiService.get('/custom-tools/tools');
      return response;
    } catch (error) {
      console.error('Error fetching available custom tools:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific custom tool
   * @param {string} toolName - Name of the tool (e.g., 'smtp')
   * @returns {Promise<Object>} Detailed tool information including actions and config fields
   */
  async getToolDetails(toolName) {
    try {
      const response = await apiService.get(`/custom-tools/tools/${toolName}`);
      return response;
    } catch (error) {
      console.error(`Error fetching details for custom tool ${toolName}:`, error);
      throw error;
    }
  }

  /**
   * Get all available actions for a specific custom tool
   * @param {string} toolName - Name of the tool (e.g., 'smtp')
   * @returns {Promise<Array>} List of actions with parameters
   */
  async getToolActions(toolName) {
    try {
      const response = await apiService.get(`/custom-tools/tools/${toolName}/actions`);
      return response;
    } catch (error) {
      console.error(`Error fetching actions for custom tool ${toolName}:`, error);
      throw error;
    }
  }

  /**
   * Get configuration template for a custom tool
   * @param {string} toolName - Name of the tool (e.g., 'smtp')
   * @param {string} provider - Configuration provider (e.g., 'gmail', 'outlook', 'office365', 'custom')
   * @returns {Promise<Object>} Configuration template with pre-filled values
   */
  async getConfigTemplate(toolName, provider = 'gmail') {
    try {
      const response = await apiService.get(
        `/custom-tools/tools/${toolName}/config-template`,
        { provider }
      );
      return response;
    } catch (error) {
      console.error(`Error fetching config template for ${toolName} with provider ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Save custom tool configuration
   * @param {Object} configData - Configuration data
   * @param {string} configData.tool_name - Name of the tool (e.g., 'smtp')
   * @param {string} configData.provider - Provider (e.g., 'gmail', 'outlook', 'office365', 'custom')
   * @param {string} configData.name - User-friendly name for this config (e.g., 'My Gmail Account')
   * @param {string} configData.description - Optional description
   * @param {Object} configData.config - Actual configuration (server, port, credentials, etc.)
   * @returns {Promise<Object>} Saved configuration response
   */
  async saveToolConfig(configData) {
    try {
      const response = await apiService.post('/custom-tools/configured', configData);
      return response;
    } catch (error) {
      console.error('Error saving custom tool config:', error);
      throw error;
    }
  }

  /**
   * Get configured custom tools for the current user
   * @param {string} toolName - Optional filter by tool name
   * @returns {Promise<Object>} Object with configs array and total count
   */
  async getConfiguredTools(toolName = null) {
    try {
      const params = toolName ? { tool_name: toolName } : {};
      const response = await apiService.get('/custom-tools/configured', params);
      return response;
    } catch (error) {
      console.error('Error fetching configured custom tools:', error);
      throw error;
    }
  }

  /**
   * Get a specific saved configuration by ID
   * @param {number} configId - Configuration ID
   * @returns {Promise<Object>} Saved configuration details
   */
  async getConfigById(configId) {
    try {
      const response = await apiService.get(`/custom-tools/configured/${configId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching config ${configId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a custom tool configuration
   * @param {string} configId - Configuration ID
   * @returns {Promise<Object>} Success status
   */
  async deleteToolConfig(configId) {
    try {
      const response = await apiService.delete(`/custom-tools/configured/${configId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting custom tool config ${configId}:`, error);
      throw error;
    }
  }

  /**
   * Update a custom tool configuration
   * @param {string} configId - Configuration ID
   * @param {Object} config - Updated configuration data
   * @returns {Promise<Object>} Updated configuration response
   */
  async updateToolConfig(configId, config) {
    try {
      const response = await apiService.put(`/custom-tools/configured/${configId}`, config);
      return response;
    } catch (error) {
      console.error(`Error updating custom tool config ${configId}:`, error);
      throw error;
    }
  }
  /**
   * Create a new custom tool definition
   * @param {Object} toolData - Tool definition data (OpenAPI spec, etc.)
   * @returns {Promise<Object>} Created tool definition
   */
  async createTool(toolData) {
    try {
      const response = await apiService.post('/custom-tools', toolData);
      return response;
    } catch (error) {
      console.error('Error creating custom tool:', error);
      throw error;
    }
  }

  /**
   * Get all custom tool definitions
   * @returns {Promise<Array>} List of custom tool definitions
   */
  async getCustomTools() {
    try {
      const response = await apiService.get('/custom-tools');
      return response;
    } catch (error) {
      console.error('Error fetching custom tools:', error);
      throw error;
    }
  }

  /**
   * Get a specific custom tool definition by ID
   * @param {string} toolId - Tool ID
   * @returns {Promise<Object>} Tool definition
   */
  async getCustomToolById(toolId) {
    try {
      const response = await apiService.get(`/custom-tools/${toolId}`);
      return response;
    } catch (error) {
      console.error(`Error fetching custom tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Update a custom tool definition
   * @param {string} toolId - Tool ID
   * @param {Object} toolData - Updated tool definition data
   * @returns {Promise<Object>} Updated tool definition
   */
  async updateTool(toolId, toolData) {
    try {
      const response = await apiService.put(`/custom-tools/${toolId}`, toolData);
      return response;
    } catch (error) {
      console.error(`Error updating custom tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a custom tool definition
   * @param {string} toolId - Tool ID
   * @returns {Promise<Object>} Useccess status
   */
  async deleteTool(toolId) {
    try {
      const response = await apiService.delete(`/custom-tools/${toolId}`);
      return response;
    } catch (error) {
      console.error(`Error deleting custom tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Test a custom tool
   * @param {string} toolId - Tool ID
   * @param {Object} testData - Test parameters
   * @returns {Promise<Object>} Test results
   */
  async testTool(toolId, testData) {
    try {
      const response = await apiService.post(`/custom-tools/${toolId}/test`, testData);
      return response;
    } catch (error) {
      console.error(`Error testing custom tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Add a new action to an existing tool
   * @param {string} toolId - Tool ID
   * @param {Object} actionData - Action definition
   * @returns {Promise<Object>} Created action
   */
  async addToolAction(toolId, actionData) {
    try {
      const response = await apiService.post(`/custom-tools/${toolId}/actions`, actionData);
      return response;
    } catch (error) {
      console.error(`Error adding action to tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing action in a tool
   * @param {string} toolId - Tool ID
   * @param {string} actionName - Action name
   * @param {Object} actionData - Updated action definition
   * @returns {Promise<Object>} Updated action
   */
  async updateToolAction(toolId, actionName, actionData) {
    try {
      const response = await apiService.put(`/custom-tools/${toolId}/actions/${actionName}`, actionData);
      return response;
    } catch (error) {
      console.error(`Error updating action ${actionName} in tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Get tool configuration (API key is masked)
   * @param {string} toolId - Tool ID
   * @returns {Promise<Object>} Tool config
   */
  async getToolConfig(toolId) {
    try {
      const response = await apiService.get(`/custom-tools/${toolId}/config`);
      return response;
    } catch (error) {
      console.error(`Error fetching config for tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch available tools from an MCP server
   * @param {Object} mcpData - Connection details (url or stdio config)
   * @returns {Promise<Array>} List of discovered tools
   */
  async fetchMcpTools(mcpData) {
    try {
      const response = await apiService.post('/custom-tools/mcp/fetch-tools', mcpData);
      return response;
    } catch (error) {
      console.error('Error fetching MCP tools:', error);
      throw error;
    }
  }

  /**
   * Get OAuth2 authorization status and redirect_uri for a custom tool
   * @param {string} toolId - Tool ID
   * @returns {Promise<Object>} Status response including redirect_uri
   */
  async getOAuthStatus(toolId) {
    try {
      const response = await apiService.get(`/custom-tools-oauth/${toolId}/status`);
      return response;
    } catch (error) {
      console.error(`Error fetching OAuth status for tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Initiate or re-run OAuth2 authorization for a custom tool
   * @param {string} toolId - Tool ID
   * @param {string} [frontendRedirectUri] - Frontend URL to redirect to after OAuth callback
   * @returns {Promise<Object>} Authorization response containing redirect/authorization URL
   */
  async authorizeOAuth(toolId, frontendRedirectUri = null) {
    try {
      const payload = frontendRedirectUri ? { frontend_redirect_uri: frontendRedirectUri } : {};
      const response = await apiService.post(`/custom-tools-oauth/${toolId}/authorize`, payload);
      return response;
    } catch (error) {
      console.error(`Error initiating OAuth authorization for tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Disconnect OAuth2 tokens for a custom tool and evict cache
   * @param {string} toolId - Tool ID
   * @returns {Promise<Object>} Disconnect status response
   */
  async disconnectOAuth(toolId) {
    try {
      const response = await apiService.post(`/custom-tools-oauth/${toolId}/disconnect`, {});
      return response;
    } catch (error) {
      console.error(`Error disconnecting OAuth for tool ${toolId}:`, error);
      throw error;
    }
  }

  /**
   * Probe an MCP server URL to check whether it supports DCR OAuth
   * @param {string} mcpServerUrl - MCP server URL to test
   * @returns {Promise<Object>} Object with { supports_dcr, mcp_server_url, error, ... }
   */
  async checkMcpDcrSupport(mcpServerUrl) {
    try {
      const response = await apiService.post('/custom-tools-oauth/mcp-dcr/check', {
        mcp_server_url: mcpServerUrl,
      });
      return response;
    } catch (error) {
      console.warn('Error checking MCP DCR support:', error);
      return { supports_dcr: false, error: error?.message || 'Check failed' };
    }
  }

  /**
   * Perform MCP Dynamic Client Registration (DCR) discovery and registration
   * @param {Object} params
   * @param {string} params.mcp_server_url - MCP server URL
   * @param {number|string} [params.tool_id] - Optional Custom Tool ID to update
   * @returns {Promise<Object>} DCR registration result with client_id, auth and token URLs
   */
  async mcpDcrDiscover({ mcp_server_url, tool_id = null }) {
    try {
      const payload = {
        mcp_server_url,
        tool_id,
      };
      const response = await apiService.post('/custom-tools-oauth/mcp-dcr', payload);
      return response;
    } catch (error) {
      console.error('Error in MCP DCR discovery:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const customToolsService = new CustomToolsService();
export default customToolsService;
